import * as React from 'react'
import type { StoredAgentVisual } from '@/lib/agent-visuals'
import { DEFAULT_AGENT_TIMEZONE } from '@/lib/timezones'
import type { AgentAudience } from '@/types/agent'

export type AgentDraft = {
  name: string
  display_name: string
  description: string
  is_enabled: boolean
  timezone: string
  base_prompt: string
  custom_instructions: string
  tool_ids: string[]
  document_ids: number[]
  audience: AgentAudience
}

export type AgentStudioPersistedDraft = {
  step: number
  visitedSteps: number[]
  draft: AgentDraft
  editorMode: 'form' | 'json'
  jsonDraft: string
  selectedModel: string
  visual: StoredAgentVisual
  coverPrompt: string
  coverStyle: string
}

const AGENT_STUDIO_DRAFT_STORAGE_KEY = 'agent-studio-draft-v1'

export const AGENT_STUDIO_TOTAL_STEPS = 4

export function createInitialAgentDraft(defaultBasePrompt: string): AgentDraft {
  return {
    name: '',
    display_name: '',
    description: '',
    is_enabled: true,
    timezone: DEFAULT_AGENT_TIMEZONE,
    base_prompt: defaultBasePrompt,
    custom_instructions: '',
    tool_ids: [],
    document_ids: [],
    audience: 'public',
  }
}

export function loadAgentStudioPersistedDraft(defaultBasePrompt: string): AgentStudioPersistedDraft | null {
  try {
    const raw = localStorage.getItem(AGENT_STUDIO_DRAFT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AgentStudioPersistedDraft>
    if (!parsed || typeof parsed !== 'object') return null
    if (!parsed.draft || typeof parsed.draft !== 'object') return null
    const step = Number(parsed.step)
    const safeStep = Number.isFinite(step) ? Math.min(Math.max(step, 1), AGENT_STUDIO_TOTAL_STEPS) : 1
    const visitedSteps = Array.isArray(parsed.visitedSteps)
      ? parsed.visitedSteps
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value >= 1 && value <= AGENT_STUDIO_TOTAL_STEPS)
      : [1]
    const draft = parsed.draft as Partial<AgentDraft>
    const normalizedDraft: AgentDraft = {
      ...createInitialAgentDraft(defaultBasePrompt),
      ...draft,
      tool_ids: Array.isArray(draft.tool_ids) ? draft.tool_ids.map((item) => String(item)) : [],
      document_ids: Array.isArray(draft.document_ids)
        ? draft.document_ids
            .map((item) => Number(item))
            .filter((item) => Number.isInteger(item) && item > 0)
        : [],
    }
    return {
      step: safeStep,
      visitedSteps: visitedSteps.length > 0 ? Array.from(new Set(visitedSteps)) : [1],
      draft: normalizedDraft,
      editorMode: parsed.editorMode === 'json' ? 'json' : 'form',
      jsonDraft: typeof parsed.jsonDraft === 'string' ? parsed.jsonDraft : '',
      selectedModel: typeof parsed.selectedModel === 'string' ? parsed.selectedModel : '',
      visual: parsed.visual && typeof parsed.visual === 'object' ? (parsed.visual as StoredAgentVisual) : {},
      coverPrompt: typeof parsed.coverPrompt === 'string' ? parsed.coverPrompt : '',
      coverStyle: typeof parsed.coverStyle === 'string' ? parsed.coverStyle : 'cinematic',
    }
  } catch {
    return null
  }
}

export function saveAgentStudioPersistedDraft(payload: AgentStudioPersistedDraft) {
  try {
    localStorage.setItem(AGENT_STUDIO_DRAFT_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore quota/storage errors; persistence is best-effort only.
  }
}

export function clearAgentStudioPersistedDraft() {
  try {
    localStorage.removeItem(AGENT_STUDIO_DRAFT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function useAgentStudioPersistedState(defaultBasePrompt: string) {
  return React.useMemo(() => loadAgentStudioPersistedDraft(defaultBasePrompt), [defaultBasePrompt])
}
